import type {
  Quest,
  QuestTemplate,
  TaskStatus,
  TemplateInstance,
  TemplateInstanceWorker,
} from "@/lib/types";
import type {
  IQuestTemplateService,
  CreateTemplateInput,
} from "@/services/interfaces/IQuestTemplateService";
import { supabase } from "@/lib/supabase";
import { callEdgeFunction } from "@/lib/supabaseAuth";
import { getChainConfig } from "@/config/contracts";
import {
  mapQuestRow,
  mapTemplateRow,
  type QuestRow,
  type QuestTemplateRow,
} from "./SupabaseQuestService";

/**
 * Stock quests over Supabase. Split out of SupabaseQuestService (DEV-318
 * review): template operations are their own port, and this file is the only
 * place their RPC names and snake_case rows appear.
 *
 * Note createTemplate goes through the `quest-templates` edge function rather
 * than calling create_quest_template directly — that RPC is service-role only,
 * because it is the function that stamps the creator identity, and the edge
 * function is what verifies the JWT it stamps from.
 */
/** The same test translate-content's isUpToDate starts from: the quest and
 *  every one of its tasks carry a non-empty translation. */
function isFullyTranslated(row: QuestRow): boolean {
  const populated = (i18n: unknown) =>
    typeof i18n === "object" && i18n !== null && Object.keys(i18n).length > 0;
  return populated(row.i18n) && (row.tasks ?? []).every((t) => populated(t.i18n));
}

type TemplateWithAnchorRow = QuestTemplateRow & {
  anchor: Pick<QuestRow, "title" | "description" | "i18n" | "source_lang"> | null;
};

/**
 * A template row carries no translation; its anchor quest does, since the
 * anchor is the listing workers read and translate-content writes to it. The
 * anchor's i18n describes the anchor's words, so it is lent to the template
 * only while both say the same thing — a drifted anchor leaves the template
 * in its author's own words rather than in a translation of different text.
 */
function mapTemplateWithAnchor({ anchor, ...row }: TemplateWithAnchorRow): QuestTemplate {
  const template = mapTemplateRow(row);
  if (!anchor || anchor.title !== row.title || anchor.description !== row.description) {
    return template;
  }
  return { ...template, i18n: anchor.i18n ?? null, sourceLang: anchor.source_lang ?? null };
}

export class SupabaseQuestTemplateService implements IQuestTemplateService {
  async createTemplate(input: CreateTemplateInput): Promise<QuestTemplate> {
    const result = await callEdgeFunction<QuestTemplateRow>("quest-templates", {
      body: {
        title: input.title,
        description: input.description,
        category: input.category,
        budgetPerQuest: input.budgetPerQuest,
        currency: input.currency,
        deadline: input.deadline,
        taskDefinition: input.taskDefinition,
        maxSlots: input.maxSlots,
        chainId: getChainConfig().chainId,
        organizationId: input.organizationId ?? null,
      },
    });
    return mapTemplateRow(result);
  }

  async listMyTemplates(userId: string): Promise<QuestTemplate[]> {
    const chainId = getChainConfig().chainId;
    const { data, error } = await supabase
      .from("quest_templates")
      // quests and quest_templates point at each other (quests.template_id
      // and this anchor_quest_id), so the embed names its FK column.
      .select("*, anchor:quests!anchor_quest_id(title, description, i18n, source_lang)")
      .eq("creator_id", userId)
      .eq("chain_id", chainId)
      .order("created_at", { ascending: false });

    if (error) throw new Error(`Failed to list templates: ${error.message}`);
    return (data as TemplateWithAnchorRow[]).map(mapTemplateWithAnchor);
  }

  async getTemplate(templateId: string): Promise<QuestTemplate | null> {
    // maybeSingle, not single: a listing the viewer cannot read comes back as
    // zero rows, and that is an answer ("no slot counts to show"), not an
    // error to throw at a panel that renders fine without them.
    const { data, error } = await supabase
      .from("quest_templates")
      .select("*")
      .eq("id", templateId)
      .maybeSingle();

    if (error) throw new Error(`Failed to load template: ${error.message}`);
    return data ? mapTemplateRow(data as QuestTemplateRow) : null;
  }

  async acceptTemplateProposal(
    templateId: string,
    proposalId: string,
    workerId: string,
    proposedPrice: number,
  ): Promise<Quest> {
    const { data, error } = await supabase.rpc("clone_quest_from_template", {
      p_template_id: templateId,
      p_worker_id: workerId,
      p_proposal_id: proposalId,
      p_proposed_price: proposedPrice,
    });

    if (error) throw new Error(`Failed to clone quest from template: ${error.message}`);

    // RPC returns the new quest ID — fetch full quest with tasks
    const newQuestId = data as string;
    this.requestCloneImages(newQuestId);
    // Read the clone back through this adapter rather than borrowing
    // IQuestService.getQuest — the two ports stay independent.
    const { data: clone, error: cloneError } = await supabase
      .from("quests")
      .select("*, tasks(*)")
      .eq("id", newQuestId)
      .single();

    if (cloneError) {
      // Unknown translation state: ask anyway. A redundant ask costs one LLM
      // call; a missing one leaves the worker reading the wrong language.
      this.requestCloneTranslation(newQuestId);
      throw new Error(`Failed to read the cloned quest: ${cloneError.message}`);
    }
    const row = clone as QuestRow;
    if (!isFullyTranslated(row)) this.requestCloneTranslation(newQuestId);
    return mapQuestRow(row);
  }

  /**
   * A clone is a new quest row created by an RPC, not by an edge function, so
   * kickQuestFollowups never sees it. Since 20260930150000 the RPC copies the
   * anchor's translation onto every row whose text matches the anchor's, so
   * this ask is only for what that copy could not cover — an anchor that was
   * never translated, or text that drifted from it. It is NOT a no-op to send
   * anyway: a copied row's source_hash is the anchor's, so translate-content
   * would buy a fresh LLM call for text it already has. Without the ask the
   * hired worker reads their own task page, workspace and My Bounties row in
   * the creator's language.
   *
   * The caller is the clone's creator, which is exactly who translate-content
   * admits on a user JWT. Fire-and-forget: the clone has already committed and
   * the proposal is already accepted, so a missed translation must not surface
   * as a failed accept. `scripts/backfill-quest-translations.ts` re-finds any
   * quest left with `i18n = '{}'`. try/catch as well as .catch because a
   * synchronous throw (no session) would escape `void p.catch()`.
   */
  private requestCloneTranslation(questId: string): void {
    try {
      void Promise.resolve(callEdgeFunction("translate-content", { body: { questId } }))
        .catch((e) => console.warn("[quest-stock] clone translation request failed", questId, e));
    } catch (e) {
      console.warn("[quest-stock] clone translation request failed", questId, e);
    }
  }

  /**
   * A clone is born `negotiating`, which is publicly visible, but its first
   * images used to come only from lockFundsAndStart — so until the creator
   * started it, the hired worker's quest page had no picture, even though the
   * listing they applied to had one. Ask here, unconditionally: quest-images
   * owns eligibility, and its claim row (quest id as PRIMARY KEY) makes the
   * later ask from lockFundsAndStart a no-op instead of a second bill.
   *
   * Same fire-and-forget contract as the translation ask: the accept has
   * already committed, and `scripts/backfill-quest-images.ts` re-finds any
   * visible quest left with no images and no claim.
   */
  private requestCloneImages(questId: string): void {
    try {
      void Promise.resolve(callEdgeFunction("quest-images", { body: { questId } }))
        .catch((e) => console.warn("[quest-stock] clone image request failed", questId, e));
    } catch (e) {
      console.warn("[quest-stock] clone image request failed", questId, e);
    }
  }

  async closeTemplate(templateId: string): Promise<QuestTemplate> {
    // 1. Fetch the template to get anchor_quest_id
    const { data: tpl, error: tplError } = await supabase
      .from("quest_templates")
      .select("*")
      .eq("id", templateId)
      .single();

    if (tplError) throw new Error(`Failed to fetch template: ${tplError.message}`);

    const anchorQuestId = (tpl as QuestTemplateRow).anchor_quest_id;

    // 2. Update template status to 'closed'
    const { data: updated, error: updateError } = await supabase
      .from("quest_templates")
      .update({ status: "closed", updated_at: new Date().toISOString() })
      .eq("id", templateId)
      .select("*")
      .single();

    if (updateError) throw new Error(`Failed to close template: ${updateError.message}`);

    // 3. Close anchor quest/task and supersede pending proposals (independent writes).
    if (anchorQuestId) {
      // Every result is checked. Discarding them left the listing "closed"
      // while its anchor task was still open and its proposals still pending —
      // a closed stock quest that could nonetheless be applied to and accepted.
      const [taskRes, questRes, proposalRes] = await Promise.all([
        supabase
          .from("tasks")
          .update({ status: "assigned" })
          .eq("quest_id", anchorQuestId)
          .eq("status", "open"),
        supabase
          .from("quests")
          .update({ status: "closed" })
          .eq("id", anchorQuestId),
        supabase
          .from("proposals")
          .update({ status: "superseded" })
          .eq("quest_id", anchorQuestId)
          .eq("status", "pending"),
      ]);

      const anchorError = taskRes.error ?? questRes.error ?? proposalRes.error;
      if (anchorError) {
        throw new Error(
          `Template ${templateId} was closed but its anchor was not fully ` +
            `retired: ${anchorError.message}. Re-run close to finish it.`,
        );
      }
    }

    return mapTemplateRow(updated as QuestTemplateRow);
  }

  async listTemplateInstances(templateId: string): Promise<TemplateInstance[]> {
    // Fetch quests spawned from this template (exclude the anchor quest)
    const { data: tpl } = await supabase
      .from("quest_templates")
      .select("anchor_quest_id")
      .eq("id", templateId)
      .single();

    const anchorQuestId = tpl?.anchor_quest_id;

    let query = supabase
      .from("quests")
      .select("*, tasks(*)")
      .eq("template_id", templateId)
      .order("created_at", { ascending: false });

    // Exclude anchor quest from the instances list
    if (anchorQuestId) {
      query = query.neq("id", anchorQuestId);
    }

    const { data, error } = await query;

    if (error) throw new Error(`Failed to list template instances: ${error.message}`);
    const rows = data as QuestRow[];

    // A clone has exactly one task, and its assignee is the worker the slot
    // was minted for. assignee_id survives completion, so this still names
    // who did the work once the instance is done.
    const assigneeOf = (row: QuestRow) =>
      row.tasks?.find((task) => task.assignee_id)?.assignee_id ?? null;
    const workers = await this.resolveWorkers(
      rows.map(assigneeOf).filter((id): id is string => !!id),
    );

    return rows.map((row) => {
      const workerId = assigneeOf(row);
      return {
        ...mapQuestRow(row),
        taskStatus: (row.tasks?.[0]?.status as TaskStatus | undefined) ?? null,
        worker: workerId
          ? (workers.get(workerId) ?? {
              id: workerId,
              displayName: null,
              handle: null,
              avatarUrl: null,
            })
          : null,
      };
    });
  }

  /**
   * Worker identities go through `public_profiles`, never a `profiles` embed:
   * `profiles` is owner-or-admin, and a denied embed comes back as 200 + null,
   * so a creator would see every worker as unknown. A second round trip for
   * the same reason as SupabaseWorkspaceService.resolveProfiles, and a failure
   * here only drops the names — it must never empty the instance list.
   */
  private async resolveWorkers(
    workerIds: string[],
  ): Promise<Map<string, TemplateInstanceWorker>> {
    const workers = new Map<string, TemplateInstanceWorker>();
    const uniqueIds = [...new Set(workerIds)];
    if (uniqueIds.length === 0) return workers;

    const { data, error } = await supabase
      .from("public_profiles")
      .select("id, display_name, handle, avatar_url")
      .in("id", uniqueIds);

    if (error) {
      console.error("[QuestTemplateService] resolveWorkers error:", error);
      return workers;
    }

    for (const p of (data ?? []) as Array<{
      id: string;
      display_name: string | null;
      handle: string | null;
      avatar_url: string | null;
    }>) {
      workers.set(p.id, {
        id: p.id,
        displayName: p.display_name,
        handle: p.handle,
        avatarUrl: p.avatar_url,
      });
    }
    return workers;
  }
}
